import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BuilderPage } from '@/app/(client)/questionnaires/-components/BuilderPage';
import {
  builderDetailQueryOptions,
  useBuilderDetailQuery,
} from '@/app/(client)/questionnaires/-hooks/useBuilderDetailQuery';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import { queryClient } from '@/lib/utils/queryClient';
import { useAdminQuestionnaireTemplateActions } from '../../../-hooks/useAdminQuestionnaireTemplateActions';

/**
 * The Retraced-admin template builder: the shared `BuilderPage` in template
 * mode, reading and saving through the management routes. The old page wrapped
 * the same builder with the admin API handed in; here that is the `admin`
 * scope plus a delete that goes through the admin bulk delete and comes back
 * to this list. No tab row — a template has no responses to switch to.
 */
export const Route = createFileRoute(
  '/management/supplier-questionnaire-templates/(pages)/$id/edit/',
)({
  // A prefetch. Its rejection is swallowed so the builder's own loading and
  // error handling stay in charge, as on the customer detail layout.
  loader: ({ params }) =>
    queryClient
      .ensureQueryData(
        builderDetailQueryOptions('template', params.id, 'admin'),
      )
      .catch(() => undefined),
  component: AdminTemplateBuilderRoute,
});

function AdminTemplateBuilderRoute() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const actions = useAdminQuestionnaireTemplateActions();
  const { data: detail } = useBuilderDetailQuery('template', id, {
    scope: 'admin',
  });
  // The template's name in the header breadcrumb rather than its id.
  useBreadcrumbLabel(id, detail?.name);

  function backToList(): void {
    void navigate({ to: '/management/supplier-questionnaire-templates' });
  }

  async function deleteThisTemplate(): Promise<void> {
    const removed = await actions.deleteTemplates([id]);
    // The actions hook has already toasted; a rejection keeps the dialog open.
    if (!removed) throw new Error('template-delete-refused');
    backToList();
  }

  return (
    <div className='flex flex-col flex-1 min-h-0 overflow-hidden'>
      <BuilderPage
        key={id}
        mode='template'
        id={id}
        scope='admin'
        onLeave={backToList}
        onDeleteConfirmed={deleteThisTemplate}
        isDeleteConfirmedPending={actions.isDeleting}
      />
    </div>
  );
}
